import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  PendingAddressVerification,
  kycApi,
} from '../../api/kyc';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';

/**
 * The Tier-3 review queue. Each pending user submitted an address document to
 * prove where they live; the reviewer checks the document against the address
 * they entered, then approves (grants Tier 3) or rejects with a reason the
 * user will see and can act on.
 */

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const Tier3Verifications: React.FC = () => {
  usePage('Tier-3 Verifications');
  const toast = useToast();

  const [items, setItems] = useState<PendingAddressVerification[] | null>(null);
  const [selected, setSelected] = useState<PendingAddressVerification | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setItems(await kycApi.listPendingAddress());
    } catch (err) {
      toast.error(err, 'Failed to load pending verifications.');
    }
  }, [toast]);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  async function decide(status: 'verified' | 'rejected', reason?: string) {
    if (!selected) return;
    if (status === 'rejected' && !reason?.trim()) {
      toast.error('A rejection reason is required — the user will see it.');
      return;
    }
    try {
      setBusy(true);
      await kycApi.decideAddress(selected.id, status, reason?.trim());
      toast.success(
        status === 'verified'
          ? `${selected.name ?? 'User'} approved — now Tier 3.`
          : `${selected.name ?? 'User'} rejected.`,
      );
      setSelected(null);
      void fetchQueue();
    } catch (err) {
      toast.error(err, 'Could not record the decision.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'var(--neutral-600)',
            maxWidth: 680,
          }}
        >
          Users awaiting Tier-3 approval. Check each address document against the
          address they entered, then approve (which upgrades them to Tier 3) or
          reject with a reason. Both outcomes notify the user by push, in-app,
          and email.
        </p>

        {items === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !items.length ? (
          <div
            style={{
              padding: 48,
              textAlign: 'center',
              fontSize: 14,
              color: 'var(--neutral-500)',
            }}
          >
            Nothing awaiting review. The queue is clear.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                minWidth: 720,
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['User', 'Address', 'Submitted', ''].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: 12,
                        color: 'var(--neutral-600)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{u.name ?? 'Unknown user'}</div>
                      <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                        {u.email ?? u.phoneNumber ?? '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--neutral-700)' }}>
                      {[u.address, u.lga, u.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--neutral-600)' }}>
                      {formatDateTime(u.submittedAt)}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <Button variant="secondary" onClick={() => setSelected(u)}>
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected ? (
        <ReviewDrawer
          user={selected}
          busy={busy}
          onDecide={decide}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
};

const ReviewDrawer: React.FC<{
  user: PendingAddressVerification;
  busy: boolean;
  onDecide: (status: 'verified' | 'rejected', reason?: string) => void;
  onClose: () => void;
}> = ({ user, busy, onDecide, onClose }) => {
  const [reason, setReason] = useState('');
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)',
          height: '100%',
          background: 'var(--neutral-100)',
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--neutral-900)' }}>
              {user.name ?? 'Unknown user'}
            </h2>
            <div style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 4 }}>
              {user.email ?? user.phoneNumber ?? '—'} · <Badge variant="info">{user.tier}</Badge>
            </div>
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>

        <Field label="Entered address" value={
          [user.address, user.landmark, user.lga, user.state].filter(Boolean).join(', ') || '—'
        } />
        {user.documentType ? <Field label="Document type" value={user.documentType} /> : null}

        <div>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 6 }}>
            Uploaded document
          </div>
          {user.documentUrl ? (
            <a href={user.documentUrl} target="_blank" rel="noreferrer">
              <img
                src={user.documentUrl}
                alt="Address document"
                style={{
                  width: '100%',
                  maxHeight: 420,
                  objectFit: 'contain',
                  borderRadius: 8,
                  border: '1px solid var(--neutral-300)',
                  background: 'var(--neutral-100)',
                }}
              />
            </a>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
              No document on file.
            </div>
          )}
        </div>

        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Rejection reason
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Required only if rejecting — the user sees this and resubmits against it."
            rows={3}
            style={{
              width: '100%',
              marginTop: 6,
              padding: 12,
              fontSize: 13,
              fontFamily: 'var(--font-family)',
              color: 'var(--neutral-800)',
              background: 'var(--neutral-100)',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Button onClick={() => onDecide('verified')} loading={busy}>
            Approve — grant Tier 3
          </Button>
          <Button variant="danger" onClick={() => onDecide('rejected', reason)} loading={busy}>
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, color: 'var(--neutral-900)', wordBreak: 'break-word' }}>
      {value}
    </div>
  </div>
);
